import { useCallback, useMemo } from "react";

import { useAddTournamentNamingKey, useLeaguesListTitle } from "@/hooks";
import { MessageKey, useT } from "@/i18n";

import { NavLinkItemType } from "./NavLink.types";

export const useGetNavLinkLabel = () => {
  const t = useT();
  const addTournamentNamingKey = useAddTournamentNamingKey();
  // Tours and leagues share one nav entry labelled "Tours & Leagues"
  // ("Circuits & Leagues" in en-US), handled below outside the static key map.
  const leaguesListTitle = useLeaguesListTitle();

  const NAV_LINK_LABELS: { [key in Exclude<NavLinkItemType, "leagues">]: MessageKey } = useMemo(
    () => ({
      clubs: "general.navigation.links.label.clubs",
      issuedLicenses: "general.navigation.links.label.issuedLicenses",
      licenses: "general.navigation.links.label.licenses",
      certificates: "general.navigation.links.label.certificates",
      issuedCertificates: "general.navigation.links.label.issuedCertificates",
      applicationForms: "general.navigation.links.label.applicationForms",
      issuedApplicationForms: "general.navigation.links.label.issuedApplicationForms",
      organizations: "general.navigation.links.label.organizations",
      platforms: "general.navigation.links.label.platforms",
      sports: "general.navigation.links.label.sports",
      disciplines: "general.navigation.links.label.disciplines",
      thirdPartyLicenses: "general.navigation.links.label.thirdPartyLicenses",
      platformManagers: "general.navigation.links.label.platformManagers",
      platformSettings: "general.navigation.links.label.platformSettings",
      organizationSettings: "general.navigation.links.label.organizationSettings",
      userSettings: "general.navigation.links.label.userSettings",
      venues: "general.navigation.links.label.venues",
      tournaments: addTournamentNamingKey("general.navigation.links.label.tournaments"),
      users: "general.navigation.links.label.users",
      specificVenue: "general.navigation.links.label.specificVenue",
      home: "general.navigation.links.label.home",
      news: "general.navigation.links.label.news",
      gallery: "general.navigation.links.label.gallery",
      documents: "general.navigation.links.label.documents",
      pages: "general.navigation.links.label.pages",
      banners: "general.navigation.links.label.banners",
      partners: "general.navigation.links.label.partners",
      transactions: "general.navigation.links.label.transactions",
      stripeConnect: "general.navigation.links.label.stripeConnect",
      platformStripeAccounts: "general.navigation.links.label.platformStripeAccounts",
      reports: "general.navigation.links.label.reports",
      paymentsHistory: "general.navigation.links.label.paymentsHistory",
      integrations: "general.navigation.links.label.integrations",
      helpCenter: "general.navigation.links.label.helpCenter",
      tutorials: "general.navigation.links.label.tutorials",
      contactSupport: "general.navigation.links.label.contactSupport",
      coaches: "general.navigation.links.label.coaches",
      teams: "general.navigation.links.label.teams",
      managementTeam: "general.navigation.links.label.managementTeam",
      auditLogs: "general.navigation.links.label.auditLogs",
      documentation: "general.navigation.links.label.documentation",
    }),
    [addTournamentNamingKey]
  );

  const getNavLinkLabel = useCallback(
    (type: NavLinkItemType) => {
      if (type === "leagues") return leaguesListTitle;
      return t(NAV_LINK_LABELS[type]);
    },
    [NAV_LINK_LABELS, t, leaguesListTitle]
  );

  return getNavLinkLabel;
};
